import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Route, Routes, useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowRight,
  Check,
  HelpCircle,
  Maximize2,
  MessageCircle,
  Radio,
  Send,
  ShieldCheck,
  Volume2,
} from 'lucide-react';
import { Badge, Button, FlexCastMark, Spinner } from '@flexcast/ui';
import { newId, type ViewerResolution } from '@flexcast/domain';
import { refreshPlayback, resolveSession, viewerId } from './api';

export function App() {
  return (
    <Routes>
      <Route path="/" element={<JoinPage />} />
      <Route path="/watch/:codeOrSlug" element={<WatchPage />} />
      <Route path="*" element={<JoinPage />} />
    </Routes>
  );
}

function JoinPage() {
  const navigate = useNavigate();
  const [digits, setDigits] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit(event: FormEvent) {
    event.preventDefault();
    const code = digits.replace(/\D/g, '');
    if (code.length !== 6) {
      setError('Enter the six-digit code shown by your presenter.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await resolveSession(code);
      void navigate(`/watch/${code}`, { state: { preloaded: true } });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to join this presentation.');
      setBusy(false);
    }
  }

  return (
    <main className="join-shell">
      <div className="join-orb join-orb--one" />
      <div className="join-orb join-orb--two" />
      <header>
        <FlexCastMark />
      </header>
      <section className="join-panel" aria-labelledby="join-title">
        <div className="join-panel__signal">
          <Radio size={24} />
          <span>Live presentation</span>
        </div>
        <h1 id="join-title">
          Join the room,
          <br />
          wherever you are.
        </h1>
        <p>Enter the presentation code shown on the screen. No account or download required.</p>
        <form onSubmit={(event) => void submit(event)} noValidate>
          <label htmlFor="presentation-code">Presentation code</label>
          <div className="code-field">
            <input
              id="presentation-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={digits}
              onChange={(event) => setDigits(event.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="482 731"
              aria-describedby={error ? 'join-error' : 'join-hint'}
              autoFocus
            />
            <Button type="submit" disabled={busy} aria-label="Join presentation">
              {busy ? <Spinner label="Joining" /> : <ArrowRight size={21} />}
            </Button>
          </div>
          {error ? (
            <p className="form-error" id="join-error">
              <AlertCircle size={16} />
              {error}
            </p>
          ) : (
            <small id="join-hint">
              Try <strong>482 731</strong> in the local preview.
            </small>
          )}
        </form>
        <div className="join-trust">
          <ShieldCheck size={18} />
          <span>Secure, private playback powered by FlexCast</span>
        </div>
      </section>
      <footer>Presentation broadcasting without the meeting clutter.</footer>
    </main>
  );
}

function WatchPage() {
  const { codeOrSlug = '' } = useParams();
  const navigate = useNavigate();
  const [resolution, setResolution] = useState<ViewerResolution>();
  const [error, setError] = useState('');
  const [pin, setPin] = useState('');
  const [needsPin, setNeedsPin] = useState(false);

  async function load(candidatePin?: string) {
    setError('');
    try {
      setResolution(await resolveSession(codeOrSlug, candidatePin));
      setNeedsPin(false);
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : 'Unable to load presentation.';
      if (/PIN/i.test(message)) setNeedsPin(true);
      else setError(message);
    }
  }

  useEffect(() => {
    void load();
  }, [codeOrSlug]);

  if (needsPin) {
    return <PinPage pin={pin} onPin={setPin} onSubmit={() => void load(pin)} />;
  }
  if (error) {
    return (
      <StatusPage title="Presentation unavailable" copy={error} onBack={() => void navigate('/')} />
    );
  }
  if (!resolution) {
    return (
      <StatusPage
        title="Preparing your presentation"
        copy="Connecting to the live broadcast…"
        loading
      />
    );
  }
  return <PlayerPage resolution={resolution} demo={!import.meta.env.VITE_API_URL} />;
}

function PinPage({
  pin,
  onPin,
  onSubmit,
}: {
  pin: string;
  onPin: (pin: string) => void;
  onSubmit: () => void;
}) {
  return (
    <main className="status-page">
      <FlexCastMark />
      <div className="status-card">
        <ShieldCheck size={34} />
        <h1>This presentation is private</h1>
        <p>Enter the PIN supplied by your presenter.</p>
        <input
          aria-label="Presentation PIN"
          inputMode="numeric"
          value={pin}
          onChange={(event) => onPin(event.target.value.replace(/\D/g, '').slice(0, 8))}
        />
        <Button onClick={onSubmit}>Continue</Button>
      </div>
    </main>
  );
}

function StatusPage({
  title,
  copy,
  loading,
  onBack,
}: {
  title: string;
  copy: string;
  loading?: boolean;
  onBack?: () => void;
}) {
  return (
    <main className="status-page">
      <FlexCastMark />
      <div className="status-card">
        {loading ? <Spinner /> : <AlertCircle size={34} />}
        <h1>{title}</h1>
        <p>{copy}</p>
        {onBack && <Button onClick={onBack}>Enter another code</Button>}
      </div>
    </main>
  );
}

function PlayerPage({ resolution, demo }: { resolution: ViewerResolution; demo: boolean }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [question, setQuestion] = useState('');
  const [sent, setSent] = useState(false);
  const socketRef = useRef<WebSocket | undefined>(undefined);
  const tokenRef = useRef(resolution.websocketToken);
  const [connection, setConnection] = useState<'connecting' | 'connected' | 'reconnecting'>(
    'connecting',
  );
  const [sessionStatus, setSessionStatus] = useState(resolution.status);
  const brand = resolution.brand;

  useEffect(() => {
    document.documentElement.style.setProperty('--brand-primary', brand.primaryColor);
    document.documentElement.style.setProperty('--brand-accent', brand.accentColor);
    if (demo) return;
    let player: { delete(): void; load(url: string): void; play(): void } | undefined;
    void import('amazon-ivs-player').then(({ create, isPlayerSupported }) => {
      if (!isPlayerSupported || !videoRef.current) return;
      const instance = create({
        wasmWorker: new URL(
          'amazon-ivs-player/dist/assets/amazon-ivs-wasmworker.min.js',
          import.meta.url,
        ).href,
        wasmBinary: new URL(
          'amazon-ivs-player/dist/assets/amazon-ivs-wasmworker.min.wasm',
          import.meta.url,
        ).href,
        serviceWorker: { url: '/amazon-ivs-service-worker-loader.js' },
      });
      instance.attachHTMLVideoElement(videoRef.current);
      const playbackUrl = new URL(resolution.playbackUrl);
      playbackUrl.searchParams.set('token', resolution.playbackToken);
      instance.load(playbackUrl.toString());
      instance.play();
      player = instance;
    });
    let active = true;
    let retryTimer: number | undefined;
    let socket: WebSocket | undefined;
    const connect = () => {
      if (!active) return;
      socket = new WebSocket(
        `${resolution.websocketUrl}?token=${encodeURIComponent(tokenRef.current)}`,
      );
      socketRef.current = socket;
      socket.onopen = () => setConnection('connected');
      socket.onmessage = (event) => {
        const message = JSON.parse(String(event.data)) as {
          type?: string;
          payload?: { status?: string };
        };
        if (message.type === 'SESSION_STATUS' && message.payload?.status) {
          setSessionStatus(message.payload.status as ViewerResolution['status']);
        }
      };
      socket.onclose = () => {
        if (!active || ['ended', 'failed'].includes(sessionStatus)) return;
        setConnection('reconnecting');
        void refreshPlayback(resolution.sessionId, tokenRef.current)
          .then((tokens) => {
            tokenRef.current = tokens.websocketToken;
            retryTimer = window.setTimeout(connect, 1_500);
          })
          .catch(() => setSessionStatus('ended'));
      };
    };
    connect();
    const refreshTimer = window.setInterval(() => {
      void refreshPlayback(resolution.sessionId, tokenRef.current)
        .then((tokens) => {
          tokenRef.current = tokens.websocketToken;
          if (!player) return;
          const playbackUrl = new URL(resolution.playbackUrl);
          playbackUrl.searchParams.set('token', tokens.playbackToken);
          player.load(playbackUrl.toString());
          player.play();
        })
        .catch(() => socket?.close());
    }, 12 * 60_000);
    return () => {
      window.clearInterval(refreshTimer);
      active = false;
      if (retryTimer) window.clearTimeout(retryTimer);
      player?.delete();
      socket?.close();
    };
  }, [brand, demo, resolution]);

  if (sessionStatus === 'ended' || sessionStatus === 'failed') {
    return (
      <StatusPage
        title="Presentation ended"
        copy="Thanks for joining this FlexCast presentation."
      />
    );
  }

  function submitQuestion(event: FormEvent) {
    event.preventDefault();
    if (!question.trim()) return;
    if (!demo && socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(
        JSON.stringify({
          id: newId(),
          type: 'QUESTION_CREATE',
          sentAt: new Date().toISOString(),
          payload: { text: question.trim(), viewerId: viewerId() },
        }),
      );
    }
    setSent(true);
    setQuestion('');
    window.setTimeout(() => setSent(false), 3500);
  }

  return (
    <div className="watch-shell">
      <header className="watch-header">
        <div className="brand-lockup">
          {brand.logoUrl ? (
            <img src={brand.logoUrl} alt="" />
          ) : (
            <span>{brand.displayName.charAt(0)}</span>
          )}
          <div>
            <small>Live from</small>
            <strong>{brand.displayName}</strong>
          </div>
        </div>
        <div className="live-pill">
          <i /> Live
        </div>
        {connection !== 'connected' && <Badge tone="warning">Reconnecting…</Badge>}
      </header>
      <main className="watch-main">
        <section className="video-frame" aria-label="Live presentation video">
          {demo ? (
            <div className="demo-feed">
              <div className="demo-window">
                <span className="demo-chip">North Sydney</span>
                <h2>105 Miller Street</h2>
                <p>Workplace experience, presented live.</p>
                <div className="demo-plan">
                  <i />
                  <i />
                  <i />
                  <i />
                </div>
              </div>
              <small>Local preview · live video appears here</small>
            </div>
          ) : (
            <video ref={videoRef} playsInline controls crossOrigin="anonymous" />
          )}
          <button
            className="fullscreen-button"
            aria-label="Enter fullscreen"
            onClick={() => void videoRef.current?.requestFullscreen()}
          >
            <Maximize2 size={18} />
          </button>
        </section>
        <aside className="audience-panel">
          <div className="welcome-copy">
            <span>
              <Volume2 size={18} />
            </span>
            <div>
              <h2>{brand.welcomeText ?? 'Welcome to the live presentation.'}</h2>
              <p>Turn up your sound and follow along with the presenter.</p>
            </div>
          </div>
          {resolution.questionsEnabled && (
            <form className="question-box" onSubmit={submitQuestion}>
              <div className="question-heading">
                <MessageCircle size={20} />
                <div>
                  <strong>Ask the presenter</strong>
                  <small>Your question appears privately on their screen.</small>
                </div>
              </div>
              <textarea
                aria-label="Ask a question"
                value={question}
                onChange={(event) => setQuestion(event.target.value.slice(0, 500))}
                placeholder="Can you show us the northern corner office?"
                rows={4}
              />
              <div>
                <small>{question.length}/500</small>
                <Button type="submit" disabled={!question.trim()}>
                  {sent ? (
                    <>
                      <Check size={17} /> Sent
                    </>
                  ) : (
                    <>
                      <Send size={17} /> Send question
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
          <div className="support-note">
            <HelpCircle size={17} />
            <span>
              {brand.supportText ??
                'If playback stops, check your connection and refresh this page.'}
            </span>
          </div>
        </aside>
      </main>
      <footer className="watch-footer">
        <FlexCastMark />
        <span>Secure presentation broadcast</span>
      </footer>
    </div>
  );
}
